import type { JSX } from 'react'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, KeyRound, Server as ServerIcon, Sliders } from 'lucide-react'
import type { BehaviourConfig } from '@apiary/shared'
import { defaultBehaviourConfig } from '@apiary/shared'
import type { CreateBotRequest } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { cn } from '@/lib/cn'
import { errorText } from '@/ui/errors'
import { useMcAccounts, useServers } from '@/hooks/queries'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { Button } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Panel, Surface } from '@/ui/primitives/Surface'
import { Select } from '@/ui/primitives/Select'
import { Switch } from '@/ui/primitives/Switch'
import { TextField } from '@/ui/primitives/Field'
import { SkinHead } from '@/ui/primitives/SkinHead'

/**
 * Создание бота: три шага — аккаунт, сервер, поведения.
 *
 * Шаги, а не одна длинная форма: за раз нужно решить одну вещь
 * (`progressive-disclosure`), и на каждом шаге видно, чего не хватает. Валидация
 * срабатывает на переходе вперёд — назад можно вернуться всегда и введённое не
 * теряется.
 *
 * Тонкую настройку поведений здесь сознательно не показываем: у нового бота
 * дефолты разумные, а полный набор (интервалы, якорь, расписание) живёт на
 * странице бота, где рядом видно, как он себя ведёт.
 */

const STEPS = [
  { key: 'account', label: 'Аккаунт', icon: KeyRound },
  { key: 'server', label: 'Сервер', icon: ServerIcon },
  { key: 'behaviours', label: 'Поведения', icon: Sliders }
] as const

export type WizardStep = (typeof STEPS)[number]['key']

export interface WizardDraft {
  name: string
  mcAccountId: string
  serverId: string
  autoStart: boolean
  behaviours: BehaviourConfig
  disclaimer: boolean
}

export interface WizardErrors {
  name?: string
  mcAccountId?: string
  serverId?: string
  disclaimer?: string
}

/**
 * Проверка шага. Вынесена из компонента, чтобы её можно было прогонять тестом
 * напрямую, а не через DOM.
 */
export function validateStep(step: WizardStep, draft: WizardDraft): WizardErrors {
  const errors: WizardErrors = {}
  if (step === 'account') {
    if (draft.name.trim() === '') errors.name = 'Введите имя бота'
    else if (!/^[A-Za-z0-9_-]{3,32}$/.test(draft.name.trim())) {
      errors.name = 'Только латиница, цифры, дефис и подчёркивание, 3–32 символа'
    }
    if (draft.mcAccountId === '') errors.mcAccountId = 'Выберите Minecraft-аккаунт'
  }
  if (step === 'server' && draft.serverId === '') {
    errors.serverId = 'Выберите профиль сервера'
  }
  if (step === 'behaviours' && !draft.disclaimer) {
    errors.disclaimer = 'Подтвердите, что отвечаете за правила сервера'
  }
  return errors
}

export function CreateBotScreen(): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)
  const accounts = useMcAccounts()
  const servers = useServers()

  const [stepIndex, setStepIndex] = useState(0)
  const [errors, setErrors] = useState<WizardErrors>({})
  const [draft, setDraft] = useState<WizardDraft>({
    name: '',
    mcAccountId: '',
    serverId: '',
    autoStart: true,
    behaviours: defaultBehaviourConfig(),
    disclaimer: false
  })

  const step = STEPS[stepIndex]?.key ?? 'account'
  const accountItems = accounts.data?.items ?? []
  const serverItems = servers.data?.items ?? []
  const account = accountItems.find(item => item.id === draft.mcAccountId) ?? null
  const server = serverItems.find(item => item.id === draft.serverId) ?? null

  const create = useMutation({
    mutationFn: (dto: CreateBotRequest) => api.bots.create(dto),
    onSuccess: bot => {
      void queryClient.invalidateQueries({ queryKey: qk.bots.root })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.summary })
      push({ message: `Бот «${bot.name}» создан`, tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
      void navigate({ to: '/bots/$botId', params: { botId: bot.id } })
    },
    onError: error =>
      push({ message: errorText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const forward = (): void => {
    const found = validateStep(step, draft)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1)
      return
    }
    create.mutate({
      name: draft.name.trim(),
      mcAccountId: draft.mcAccountId,
      serverId: draft.serverId,
      autoStart: draft.autoStart,
      behaviours: draft.behaviours
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
      {/* Прогресс: где я и сколько осталось. */}
      <ol
        className="flex items-center gap-2"
        aria-label={`Шаг ${stepIndex + 1} из ${STEPS.length}`}
      >
        {STEPS.map((item, index) => {
          const done = index < stepIndex
          const current = index === stepIndex
          return (
            <li key={item.key} className="flex min-w-0 flex-1 items-center gap-2">
              <span
                aria-current={current ? 'step' : undefined}
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-2 rounded-sm border px-2.5 py-2 text-2xs transition-colors duration-140',
                  current
                    ? 'border-honey/50 bg-honey/10 text-ink'
                    : done
                      ? 'border-line bg-panel-2 text-ink-muted'
                      : 'border-line bg-panel text-ink-faint'
                )}
              >
                {done ? (
                  <Check aria-hidden className="size-3.5 text-live" />
                ) : (
                  <item.icon aria-hidden className="size-3.5" />
                )}
                <span className="truncate">{item.label}</span>
              </span>
            </li>
          )
        })}
      </ol>

      {step === 'account' ? (
        <Panel title="Аккаунт и имя" bodyClassName="flex flex-col gap-4">
          <TextField
            label="Имя бота"
            hint="Видно только в панели: по нему вы отличаете ботов друг от друга"
            value={draft.name}
            required
            autoFocus
            error={errors.name ?? null}
            onChange={event => setDraft({ ...draft, name: event.target.value })}
          />

          {accounts.isPending ? (
            <Surface className="shimmer h-16" />
          ) : accountItems.length === 0 ? (
            <Empty
              title="Нет ни одного Minecraft-аккаунта"
              hint="Бот входит на сервер под аккаунтом — добавьте его на экране «Аккаунты»."
              action={{ label: 'К аккаунтам', onClick: () => void navigate({ to: '/accounts' }) }}
            />
          ) : (
            <>
              <Select
                label="Minecraft-аккаунт"
                value={draft.mcAccountId}
                error={errors.mcAccountId ?? null}
                placeholder="Выберите аккаунт"
                onValueChange={value => setDraft({ ...draft, mcAccountId: value })}
                options={accountItems.map(item => ({
                  value: item.id,
                  label: item.gamertag ?? item.label,
                  note:
                    item.lockedByBotId === null
                      ? item.status === 'READY'
                        ? 'готов'
                        : 'нужна авторизация'
                      : 'занят ботом',
                  disabled: item.lockedByBotId !== null
                }))}
              />
              {account === null ? null : (
                <div className="flex items-center gap-3 rounded-sm border border-line bg-panel-2 p-3">
                  <SkinHead
                    uuid={account.mcUuid}
                    name={account.gamertag ?? account.label}
                    size={44}
                  />
                  <div className="min-w-0">
                    <p className="truncate font-display text-base font-semibold text-ink">
                      {account.gamertag ?? account.label}
                    </p>
                    <p className="truncate font-mono text-2xs text-ink-muted">
                      {account.msaEmail ??
                        (account.authType === 'OFFLINE' ? 'offline-аккаунт' : '—')}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </Panel>
      ) : null}

      {step === 'server' ? (
        <Panel title="Куда подключаться" bodyClassName="flex flex-col gap-4">
          {servers.isPending ? (
            <Surface className="shimmer h-16" />
          ) : serverItems.length === 0 ? (
            <Empty
              title="Нет профилей серверов"
              hint="Добавьте сервер: хост, порт и, если нужно, точную версию."
              action={{ label: 'К серверам', onClick: () => void navigate({ to: '/servers' }) }}
            />
          ) : (
            <>
              <Select
                label="Профиль сервера"
                value={draft.serverId}
                error={errors.serverId ?? null}
                placeholder="Выберите сервер"
                onValueChange={value => setDraft({ ...draft, serverId: value })}
                options={serverItems.map(item => ({
                  value: item.id,
                  label: item.name,
                  note: `${item.host}:${item.port}`
                }))}
              />
              {server === null ? null : (
                <div className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-panel-2 p-3">
                  <span className="font-mono text-2xs text-ink">
                    {server.host}:{server.port}
                  </span>
                  <Chip tone={server.probe?.online === true ? 'live' : 'idle'} dot>
                    {server.probe?.online === true ? 'онлайн' : 'нет данных'}
                  </Chip>
                  {server.probe?.version === null || server.probe?.version === undefined ? null : (
                    <Chip mono>{server.probe.version}</Chip>
                  )}
                  {server.probe?.supportedByCore === false ? (
                    <Chip tone="warn" dot>
                      ядро не знает эту версию
                    </Chip>
                  ) : null}
                </div>
              )}
            </>
          )}
        </Panel>
      ) : null}

      {step === 'behaviours' ? (
        <Panel title="Как себя вести" bodyClassName="flex flex-col gap-4">
          <Switch
            label="Запускать сразу после создания"
            hint="И поднимать автоматически, когда runner перезапустится"
            checked={draft.autoStart}
            onCheckedChange={autoStart => setDraft({ ...draft, autoStart })}
          />
          <Switch
            label="Анти-AFK"
            hint="Микродействия со случайным интервалом, чтобы сервер не выкинул за простой"
            checked={draft.behaviours.antiAfk.enabled}
            onCheckedChange={enabled =>
              setDraft({
                ...draft,
                behaviours: {
                  ...draft.behaviours,
                  antiAfk: { ...draft.behaviours.antiAfk, enabled }
                }
              })
            }
          />
          <Switch
            label="Автоеда"
            hint="Бот сам поест, когда сытость упадёт"
            checked={draft.behaviours.autoEat.enabled}
            onCheckedChange={enabled =>
              setDraft({
                ...draft,
                behaviours: {
                  ...draft.behaviours,
                  autoEat: { ...draft.behaviours.autoEat, enabled }
                }
              })
            }
          />

          <div className="rounded-sm border border-line bg-panel-2 p-3">
            <Switch
              label="Я отвечаю за соблюдение правил сервера"
              hint="Многие сервера запрещают ботов и AFK-фарм. Панель не проверяет правила за вас."
              checked={draft.disclaimer}
              onCheckedChange={disclaimer => setDraft({ ...draft, disclaimer })}
            />
            {errors.disclaimer === undefined ? null : (
              <p role="alert" className="mt-2 text-2xs text-fail">
                {errors.disclaimer}
              </p>
            )}
          </div>
        </Panel>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() =>
            stepIndex === 0 ? void navigate({ to: '/bots' }) : setStepIndex(stepIndex - 1)
          }
        >
          <ArrowLeft aria-hidden className="size-4" />
          {stepIndex === 0 ? 'Отмена' : 'Назад'}
        </Button>

        <Button variant="primary" loading={create.isPending} onClick={forward}>
          {stepIndex === STEPS.length - 1 ? 'Создать бота' : 'Далее'}
          {stepIndex === STEPS.length - 1 ? (
            <Check aria-hidden className="size-4" />
          ) : (
            <ArrowRight aria-hidden className="size-4" />
          )}
        </Button>
      </div>
    </div>
  )
}

function Empty({
  title,
  hint,
  action
}: {
  title: string
  hint: string
  action: { label: string; onClick: () => void }
}): JSX.Element {
  return (
    <Surface className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <p className="font-display text-base font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-base text-ink-muted">{hint}</p>
      <Button className="mt-2" onClick={action.onClick}>
        {action.label}
      </Button>
    </Surface>
  )
}
